import { SignUpState } from "../../../store/types.tsx";
import handleInitiateSignUp from "./handleInitiateSignUp.tsx";
import handleAgreeToTermsAndConditions from "./handleAgreeToTermsAndConditions.tsx";
import handleAgreeToDataProtectionAgreement from "./handleAgreeToDataProtectionAgreement.ts";
import handleSignupToken from "./handleSignupToken.tsx";
import handleChooseSSOPlatform from "./handleChooseSSOPlatform.tsx";
import handleCompanyName from "./handleCompanyName.tsx";
import { updateSignUpState } from "../../../store/auth.tsx";
import { pushAnalyticsEvent } from "../../../utils/googleEvent.ts";
import { SIGNUP_PROVIDER_STORAGE_KEY } from "../../../const.ts";

interface HandleSignUpProps {
  searchText: string;
  dispatch: any;
  signUpState: SignUpState | undefined | null;
}

const NO_PROVIDER = "not_set";

const normalizeProvider = (value: string | null | undefined): string => {
  if (!value) {
    return NO_PROVIDER;
  }

  const normalizedValue = value.trim().toLowerCase();
  if (normalizedValue === "google") {
    return "google";
  }
  if (normalizedValue === "microsoft") {
    return "microsoft";
  }
  if (normalizedValue === "visma connect" || normalizedValue === "e-conomic") {
    return "visma_connect";
  }

  return NO_PROVIDER;
};

const getProvider = (signUpState: SignUpState | undefined | null): string => {
  if (signUpState?.provider) {
    return normalizeProvider(signUpState.provider);
  }

  return normalizeProvider(localStorage.getItem(SIGNUP_PROVIDER_STORAGE_KEY));
};

const getCompanyNameEntered = (
  companyName: string | null | undefined,
): "yes" | "no" => {
  return companyName && companyName.trim() !== "" ? "yes" : "no";
};

const handleSignUp = async ({
  searchText,
  dispatch,
  signUpState,
}: HandleSignUpProps) => {
  const trimmedText = searchText.trim().toLowerCase();
  const originalText = searchText.trim();

  if (!signUpState) {
    localStorage.removeItem(SIGNUP_PROVIDER_STORAGE_KEY);
    pushAnalyticsEvent("signup_started", {
      step_number: 1,
      entry_point: "prompt",
      provider: NO_PROVIDER,
      company_name_entered: "no",
    });
    return handleInitiateSignUp(trimmedText, dispatch);
  }

  if (!signUpState.signUpToken) {
    const provider = normalizeProvider(trimmedText);
    if (provider !== NO_PROVIDER) {
      localStorage.setItem(SIGNUP_PROVIDER_STORAGE_KEY, provider);
      dispatch(updateSignUpState({ provider }));
    }

    pushAnalyticsEvent("provider_selected", {
      step_number: 2,
      step_name: "select_provider",
      provider,
      company_name_entered: getCompanyNameEntered(signUpState.companyName),
    });
    return handleChooseSSOPlatform(trimmedText, dispatch);
  }

  if (!signUpState.termsAndConditionsHash) {
    return handleSignupToken(trimmedText, dispatch);
  }

  if (!signUpState.companyName) {
    const provider = getProvider(signUpState);
    const companyNameEntered =
      originalText.trim() !== ""
        ? "yes"
        : getCompanyNameEntered(signUpState.companyName);
    pushAnalyticsEvent("company_name", {
      step_number: 3,
      step_name: "company_name",
      provider,
      company_name_entered: companyNameEntered,
      company_name: trimmedText,
    });
    return await handleCompanyName(originalText, dispatch, provider);
  }

  if (!signUpState.dataProtectionPolicyHash) {
    const provider = getProvider(signUpState);
    pushAnalyticsEvent("data_protection_policy", {
      step_number: 5,
      step_name: "data_protection_policy",
      provider,
      company_name_entered: getCompanyNameEntered(signUpState.companyName),
    });
    return handleAgreeToTermsAndConditions(trimmedText, dispatch);
  }

  if (!signUpState.isComplete) {
    const provider = getProvider(signUpState);
    pushAnalyticsEvent("data_protection_policy_accepted", {
      step_number: 6,
      step_name: "accept_data_protection_policy",
      provider,
      company_name_entered: getCompanyNameEntered(signUpState.companyName),
    });
    return handleAgreeToDataProtectionAgreement(
      trimmedText,
      dispatch,
      signUpState,
    );
  }
};
export default handleSignUp;
